import cx from 'classnames'
import { Children, type PropsWithChildren, Suspense, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { IconButton } from '../../actions/iconButton/IconButton'
import { ErrorFallback } from '../../feedback/errorFallback/ErrorFallback'
import { FullscreenSpinner } from '../../feedback/fullscreenSpinner/FullscreenSpinner'
import { useTranslate } from '../../hooks'
import { paperStyles } from '../../libs/paperStyles'

export type Props = PropsWithChildren & {
  ErrorFallback?: typeof ErrorFallback
  className?: string
}

export const SidebarLayout = ({
  ErrorFallback: CustomErrorFallback,
  children,
  className,
}: Props) => {
  const translate = useTranslate()
  const [isSidebarVisible, setIsSidebarVisible] = useState(true)
  const toggleSidebarVisibility = () => setIsSidebarVisible(!isSidebarVisible)

  return (
    <div
      className={cx(
        'flex items-stretch pe-3',
        paperStyles(), // Needed since the page now has a dark background for scroll shadow reasons
        className,
      )}
    >
      <div
        className={cx(
          'flex items-stretch relative -ms-px px-px cursor-pointer group/sidebar',
        )}
        onClick={toggleSidebarVisibility}
      >
        <div
          className={cx(
            paperStyles({ borders: ['left'] }),
            'w-px group-hover/sidebar:border-primary transition-colors',
          )}
        />
        <IconButton
          onClick={toggleSidebarVisibility}
          tip={isSidebarVisible ? translate('close') : translate('open')}
          iconName={isSidebarVisible ? 'SidebarClose' : 'SidebarOpen'}
          iconSize="sm"
          className={cx(
            'absolute! top-3 left-1/2 -translate-x-1/2 group-hover/sidebar:inset-ring-primary! opacity-0 group-hover/sidebar:opacity-100 max-w-[unset]!',
            paperStyles({ ringColor: 'grey' }),
            'bg-amaze-paper! dark:bg-amaze-paperDark!',
            !isSidebarVisible && 'opacity-100',
          )}
        />
      </div>
      {isSidebarVisible && (
        <div className="flex flex-col min-h-0 *:basis-0 *:grow *:min-h-36 *:max-h-max w-130 grow gap-y-2 py-3 ps-3">
          {Children.toArray(children).map((child, index) => (
            <ErrorBoundary
              // oxlint-disable-next-line react/no-array-index-key -- we don't have a unique key
              key={index}
              FallbackComponent={CustomErrorFallback ?? ErrorFallback}
            >
              <Suspense fallback={<FullscreenSpinner position="relative" />}>
                {child}
              </Suspense>
            </ErrorBoundary>
          ))}
        </div>
      )}
    </div>
  )
}
